import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Store, Calendar, TrendingUp, ShieldCheck, Check } from "lucide-react";
import TopBar from "@/components/TopBar";
import Reveal from "@/components/landing/Reveal";
import AnimatedProgress from "@/components/landing/AnimatedProgress";
import Footer from "@/components/landing/sections/Footer";
import { MXN, StageBadge, toFullUrl, SUC_ESTADO } from "@/components/landing/utils";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

/**
 * ProjectDetail — /proyecto/:code
 * Detailed premium view of a single project (capital, sucursales, gallery).
 * Data comes from content_blocks.projects.items[code].
 */
function SucursalMedia({ s, rich }) {
  const imgs = [s.image_url, ...(s.gallery || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).map(toFullUrl);
  const [cur, setCur] = useState(0);
  if (!imgs.length) {
    return (
      <div className={`${rich ? "min-h-[200px]" : "aspect-[16/10]"} bg-gradient-to-br from-[#F4F4F5] to-[#E4E4E7] flex items-center justify-center`}>
        <Store size={40} className="text-[#0A0A0A]/25" />
      </div>
    );
  }
  if (!rich) {
    return (
      <div className="aspect-[16/10] bg-[#E4E4E7] overflow-hidden relative">
        <img loading="lazy" decoding="async" src={imgs[0]} alt={s.nombre} className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
      </div>
    );
  }
  return (
    <div className="bg-[#0A0A0A] p-3 md:p-4 flex flex-col gap-3" data-testid="sucursal-media">
      <div className="relative flex-1 min-h-[260px] md:min-h-[340px] rounded-xl overflow-hidden bg-[#0A0A0A]">
        <img key={imgs[cur]} loading="lazy" decoding="async" src={imgs[cur]} alt={s.nombre} className="absolute inset-0 w-full h-full object-contain fade-up" />
      </div>
      {imgs.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {imgs.map((u, k) => (
            <button key={u} type="button" onClick={() => setCur(k)} data-testid={`sucursal-thumb-${k}`} className={`aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors ${k === cur ? "border-[#FFBF57]" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <img loading="lazy" decoding="async" src={u} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectDetail() {
  const { code } = useParams();
  const [project, setProject] = useState(null);
  const [brand, setBrand] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const sucNames = (project?.sucursales || []).map((s) => s.nombre).join(", ");
  useSeo({
    title: project ? `Proyecto ${project.code} · ${project.city}` : notFound ? "Proyecto no disponible" : "Proyecto de expansión",
    description: project ? `Proyecto ${project.code} de Rubí Perfumería: ${project.type || "participación en nuevas aperturas"}. Capital objetivo ${MXN.format(Number(project.capital_target) || 0)}. Sucursales: ${sucNames}.` : "Proyecto de participación de Rubí Perfumería.",
    path: `/proyecto/${encodeURIComponent(code || "")}`,
    noindex: notFound,
    image: project?.image_url ? toFullUrl(project.image_url) : undefined,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/content");
        const items = data?.projects?.items || [];
        const p = items.find((x) => (x.code || "").toLowerCase() === (code || "").toLowerCase());
        setBrand(data?.brand || null);
        if (!p) {
          setNotFound(true);
        } else {
          setProject(p);
        }
      } catch (_e) {
        setNotFound(true);
      }
    })();
  }, [code]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
        <TopBar variant="public" />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <div className="text-[11px] uppercase tracking-widest text-[#52525B] font-mono">Proyecto no encontrado</div>
          <h1 className="mt-4 font-display text-4xl">Este proyecto no está disponible</h1>
          <p className="mt-4 text-[#3F3F46]">Revisa el catálogo de proyectos actualizado.</p>
          <Link to="/#proyectos" className="btn-primary mt-8 inline-flex" data-testid="project-back-cta">
            <ArrowLeft size={14} /> Volver a proyectos
          </Link>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#52525B]">Cargando proyecto…</div>
      </div>
    );
  }

  const target = Number(project.capital_target) || 0;
  const committed = Number(project.capital_committed) || 0;
  const pct = target ? Math.min(100, (committed / target) * 100) : 0;
  const remaining = Math.max(0, target - committed);
  const heroImg = toFullUrl(project.image_url);
  const sucursales = project.sucursales || [];
  const gallery = project.gallery || (project.sucursales || []).filter((s) => s.image_url).map((s) => ({ image_url: s.image_url, title: s.nombre }));
  const activeImage = gallery[galleryIdx]?.image_url;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <TopBar variant="public" />
      <JsonLd id="breadcrumb" data={breadcrumbSchema([{ name: "Inicio", path: "/" }, { name: "Proyectos", path: "/#proyectos" }, { name: `Proyecto ${project.code}`, path: `/proyecto/${encodeURIComponent(code || "")}` }])} />

      {/* Hero */}
      <section className="relative section-dark pt-10 md:pt-14 pb-16 md:pb-24 overflow-hidden">
        <div className="gold-glow" style={{ width: 560, height: 560, top: -120, right: -160, opacity: 0.55 }} />
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <Link to="/#proyectos" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#52525B] font-mono hover:text-[#0A0A0A] transition-colors mb-8" data-testid="project-breadcrumb">
              <ArrowLeft size={12} /> Volver a proyectos
            </Link>
          </Reveal>

          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
            <div>
              <Reveal delay={80}>
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <StageBadge stage={project.stage} />
                  {project.type && <span className="pill pill-gold">{project.type}</span>}
                </div>
              </Reveal>
              <Reveal delay={160}>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#0A0A0A] leading-[1.02]">
                  {project.code}
                </h1>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-4 flex items-center gap-2 text-[#3F3F46]">
                  <MapPin size={16} className="text-[#FFBF57]" />
                  <span className="text-base md:text-lg">{project.city}</span>
                </div>
              </Reveal>
              {project.note && (
                <Reveal delay={320}>
                  <p className="mt-6 text-[#3F3F46] text-base md:text-lg leading-relaxed max-w-2xl">{project.note}</p>
                </Reveal>
              )}
              <Reveal delay={400}>
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {project.sqm && <FactBadge icon={<Store size={14} />} label="Superficie" value={project.sqm} />}
                  {project.opens && <FactBadge icon={<Calendar size={14} />} label="Apertura" value={project.opens} />}
                  {project.traffic && <FactBadge icon={<TrendingUp size={14} />} label="Flujo" value={project.traffic} />}
                  {(project.sucursales_target ?? sucursales.length) > 0 && (
                    <FactBadge icon={<Store size={14} />} label="Sucursales" value={`${sucursales.length}${project.sucursales_target ? `/${project.sucursales_target}` : ""}`} />
                  )}
                </div>
              </Reveal>
              <Reveal delay={480}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/calificacion" state={{ project: project.code }} className="btn-primary" data-testid="project-cta-primary">
                    Aplicar a {project.code} <ArrowUpRight size={16} />
                  </Link>
                  <a href="#capital" className="btn-outline">
                    Ver progreso de capital
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Hero image */}
            <Reveal delay={200} scale>
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-black/10 shadow-2xl bg-black">
                {heroImg ? (
                  <img loading="lazy" decoding="async" src={heroImg} alt={project.code} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#0F0F13] to-[#1A1A22] flex items-center justify-center">
                    <Store size={64} className="text-[#0A0A0A]/30" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 pill pill-gold text-[10px]">
                  Proyecto {project.code}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Capital progress */}
      <section id="capital" className="section-elev py-20 md:py-28 relative overflow-hidden">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <Reveal>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#52525B] font-mono">Progreso de capital · en vivo</div>
                <h2 className="mt-3 font-display text-3xl md:text-4xl text-[#0A0A0A] leading-tight">
                  Capital comprometido en <span className="accent">tiempo real</span>
                </h2>
                <p className="mt-4 text-[#3F3F46] leading-relaxed">
                  Cada apertura Rubí se financia con la participación de un círculo íntimo de asociados patrimoniales. Este es el estatus actual del proyecto.
                </p>
              </div>
            </Reveal>
            <Reveal delay={200} scale>
              <div className="glass-elev p-8 md:p-10">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-[11px] uppercase tracking-widest text-[#52525B] font-mono">Comprometido</span>
                  <span className="text-[11px] font-mono text-[#0A0A0A]">{pct.toFixed(0)}%</span>
                </div>
                <div className="text-4xl md:text-5xl num-mono text-gold-gradient" data-testid="project-capital-committed">
                  {MXN.format(committed)}
                </div>
                <AnimatedProgress value={pct} />
                <div className="mt-5 grid grid-cols-2 gap-4 pt-5 border-t border-black/8">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono">Objetivo total</div>
                    <div className="mt-1 text-xl num-mono text-[#0A0A0A]">{MXN.format(target)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono">Restante</div>
                    <div className="mt-1 text-xl num-mono text-[#FFBF57]">{MXN.format(remaining)}</div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-black/8 flex items-center gap-3 text-[11px] text-[#52525B]">
                  <ShieldCheck size={14} className="text-[#0A0A0A]" />
                  <span>Formalizado bajo estructura AEP · Contrato revisado por notario público</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Sucursales */}
      {sucursales.length > 0 && (
        <section className="section-cream on-light py-20 md:py-28">
          <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 mb-10 md:mb-14" data-testid="project-seo-intro">
            <Reveal>
              <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 lg:gap-12 items-start">
                <h2 className="font-display text-3xl md:text-4xl text-[#0A0A0B] leading-[1.05]">¿Qué es el Proyecto {project.code} de Rubí Perfumería?</h2>
                <div className="space-y-4 text-sm md:text-base text-[#3F3F46] leading-relaxed">
                  <p>El Proyecto {project.code} es un vehículo de participación privada bajo el modelo de Asociación en Participación (AEP) con el que Rubí Perfumería financia la apertura de nuevas sucursales en {project.city}. Los participantes aportan capital para el acondicionamiento, inventario inicial, equipamiento, marketing local y formalización de cada punto de venta, y a cambio reciben una participación proporcional en las utilidades generadas por las sucursales del proyecto durante la vigencia del contrato.</p>
                  <p>Rubí Perfumería es una empresa mexicana con más de 20 años de experiencia en perfumería, {sucursales.length > 0 ? `y este proyecto contempla ${project.sucursales_target || sucursales.length} aperturas: ${sucNames}.` : ""} La operación, el abastecimiento desde sus centros de distribución, la capacitación del personal y la administración corren a cargo de Rubí, de modo que el participante no opera el negocio: recibe reportes financieros mensuales, el pago de utilidades los primeros 10 días de cada mes y acceso a un portal privado con la documentación del proyecto.</p>
                  <p>El capital objetivo del proyecto es de {MXN.format(Number(project.capital_target) || 0)} y la participación mínima es de $250,000 MXN. Al término de la vigencia de cinco años, Rubí recompra la participación y el inversionista puede incorporarse con preferencia a los siguientes proyectos de expansión. Esta información es de carácter informativo y no constituye una oferta pública de valores ni una promesa de rendimiento; las condiciones definitivas se establecen en el contrato AEP.</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
            <div className="mb-10">
              <Reveal><div className="eyebrow">Composición del proyecto</div></Reveal>
              <Reveal delay={120}>
                <h2 className="mt-3 font-display text-3xl md:text-4xl text-[#0A0A0A]">Sucursales que forman el proyecto</h2>
              </Reveal>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sucursales.map((s, i) => {
                const e = SUC_ESTADO[s.estado] || { label: (s.estado || "planificado").replace(/_/g, " "), cls: "text-[#52525B] bg-black/[0.03]" };
                const stateCls = e.cls + " border-black/5";
                const rich = s.descripcion_larga || (s.highlights || []).length > 0;
                return (
                  <Reveal key={i} delay={i * 80} className={rich ? "md:col-span-2 lg:col-span-3" : ""}>
                    <div className={`card-light overflow-hidden h-full ${rich ? "grid lg:grid-cols-[0.9fr_1.1fr]" : ""}`} data-testid={`project-sucursal-${i}`}>
                      <SucursalMedia s={s} rich={rich} />
                      <div className={rich ? "p-6 md:p-8" : "p-5"}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            {s.tipo && <div className="text-[10px] uppercase tracking-widest text-[#FFBF57] font-mono mb-1">{s.tipo}</div>}
                            <div className={`font-display text-[#0A0A0B] ${rich ? "text-2xl md:text-3xl" : "text-lg"}`}>{s.nombre}</div>
                            <div className="text-xs text-[#52525B] flex items-center gap-1.5 mt-1"><MapPin size={11} /> {s.ciudad}</div>
                          </div>
                          <span className={`text-[9px] uppercase tracking-widest font-mono px-2 py-1 rounded border shrink-0 ${stateCls}`}>{e.label}</span>
                        </div>
                        {s.descripcion && rich && <p className="text-sm text-[#3F3F46] mt-2">{s.descripcion}</p>}
                        {(s.highlights || []).length > 0 && (
                          <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid={`project-sucursal-${i}-highlights`}>
                            {s.highlights.map((h, k) => (
                              <li key={k} className="flex items-start gap-2 text-[13px] text-[#0A0A0A]"><Check size={13} className="text-[#FFBF57] mt-0.5 shrink-0" strokeWidth={2.5} />{h}</li>
                            ))}
                          </ul>
                        )}
                        {s.descripcion_larga && (
                          <div className="mt-6 pt-5 border-t border-black/6 space-y-3 text-sm text-[#3F3F46] leading-relaxed" data-testid={`project-sucursal-${i}-long`}>
                            {String(s.descripcion_larga).split(/\n\s*\n|\n/).filter(Boolean).map((para, k) => <p key={k}>{para}</p>)}
                          </div>
                        )}
                        {s.superficie && (
                          <div className="text-[11px] text-[#3F3F46] mt-2 pt-2 border-t border-black/6">
                            <span className="text-[#52525B]">Superficie: </span><span className="text-[#0A0A0A]">{s.superficie}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="section-dark py-20 md:py-28">
          <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
            <div className="mb-10">
              <Reveal><div className="eyebrow">Galería</div></Reveal>
              <Reveal delay={120}>
                <h2 className="mt-3 font-display text-3xl md:text-4xl text-[#0A0A0A]">Sucursales reales, no renders</h2>
              </Reveal>
            </div>
            <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-4">
              {/* Main image */}
              <Reveal scale>
                <div className="aspect-[16/10] rounded-3xl overflow-hidden bg-black border border-black/10">
                  {activeImage ? (
                    <img loading="lazy" decoding="async" src={toFullUrl(activeImage)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1B1B21] to-[#0A0A0B]" />
                  )}
                </div>
              </Reveal>
              {/* Thumbnails */}
              <div className="grid grid-cols-3 lg:grid-cols-2 gap-3">
                {gallery.slice(0, 6).map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setGalleryIdx(i)}
                    data-testid={`project-gallery-thumb-${i}`}
                    className={`aspect-square rounded-2xl overflow-hidden bg-[#E4E4E7] border transition-all ${i === galleryIdx ? "border-[#FFBF57] ring-2 ring-[#FFBF57]/30" : "border-black/10 hover:border-[#FFBF57]/40"}`}
                  >
                    {g.image_url ? (
                      <img loading="lazy" decoding="async" src={toFullUrl(g.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-onyx on-onyx py-20 md:py-28">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <Reveal><span className="eyebrow">Convocatoria activa</span></Reveal>
          <Reveal delay={120}>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-[#0A0A0A]">
              ¿Te interesa participar en <span className="accent">{project.code}</span>?
            </h2>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-5 text-[#3F3F46] text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              Completa el cuestionario privado y agenda una reunión con dirección para conocer los detalles operativos y financieros.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <Link to="/calificacion" state={{ project: project.code }} className="btn-primary mt-10 inline-flex" data-testid="project-cta-final">
              Aplicar a este proyecto <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer brand={brand} />
    </div>
  );
}

function FactBadge({ icon, label, value }) {
  return (
    <div className="glass px-4 py-3">
      <div className="flex items-center gap-2 text-[9px] uppercase tracking-widest text-[#52525B] font-mono mb-1">
        {icon} {label}
      </div>
      <div className="text-sm text-[#0A0A0A] font-medium truncate">{value}</div>
    </div>
  );
}
